import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Plus,
  Search,
  Calendar,
  DollarSign,
  FileText,
  Eye,
  Edit,
  Send,
  Download,
  Check,
  X,
  Clock,
  Trash2,
} from "lucide-react";
import { estimateService } from "../services/estimateService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";

export default function Estimates() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const { data: estimatesData, isLoading: loading, error } = useQuery({
    queryKey: ['estimates'],
    queryFn: () => estimateService.getAll()
  });
  
  const [estimates, setEstimates] = useState([]);
  
  const deleteEstimateMutation = useMutation({
    mutationFn: (estimateId) => estimateService.delete(estimateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      toast({ description: "Estimate deleted successfully!" });
      setDeleteDialogOpen(false);
      setEstimateToDelete(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to delete estimate"
      });
      setDeleteDialogOpen(false);
      setEstimateToDelete(null);
    }
  });
  
  // Update estimates when API data changes
  useEffect(() => {
    if (estimatesData?.data) {
      setEstimates(estimatesData.data);
    }
  }, [estimatesData]);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [estimateToDelete, setEstimateToDelete] = useState(null);

  // Handle delete estimate - show confirmation dialog
  const handleDeleteEstimate = (estimateId, estimateName) => {
    setEstimateToDelete({ id: estimateId, name: estimateName });
    setDeleteDialogOpen(true);
  };

  // Confirm delete estimate
  const confirmDeleteEstimate = async () => {
    if (!estimateToDelete) return;
    deleteEstimateMutation.mutate(estimateToDelete.id);
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setEstimateToDelete(null);
  };

  // Download PDF handler
  const handleDownloadPDF = async (estimateId, projectName) => {
    try {
      toast({
        description: "Generating PDF..."
      });

      // Get backend URL and token
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const token = localStorage.getItem('auth_token');
      
      // Fetch PDF from backend
      const response = await fetch(`${backendUrl}/api/estimates/${estimateId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Estimate_${projectName || 'Untitled'}_${new Date().toISOString().slice(0, 10)}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        description: "PDF downloaded successfully!"
      });
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      toast({
        variant: "destructive",
        description: "Failed to generate PDF. Please try again."
      });
    }
  };

  const filteredEstimates = estimates.filter(estimate => 
    (estimate.client_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (estimate.project_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-500';
      case 'pending':
        return 'bg-yellow-500';
      case 'rejected':
        return 'bg-red-500';
      case 'expired':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <Check className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'rejected':
        return <X className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const totalEstimateValue = estimates.reduce((sum, est) => sum + est.total, 0);
  const acceptedEstimates = estimates.filter(est => est.status === 'accepted').length;
  const pendingEstimates = estimates.filter(est => est.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Estimates & Proposals</h1>
          <p className="text-gray-600 mt-1">Create and manage project estimates and proposals</p>
        </div>
        <Link to="/estimates/create">
          <Button className="bg-brand-600 hover:bg-brand-700">
            <Plus className="mr-2 h-4 w-4" />
            New Estimate
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Estimates</CardTitle>
            <FileText className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{estimates.length}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingEstimates}</div>
            <p className="text-xs text-muted-foreground">Awaiting response</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
            <Check className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{acceptedEstimates}</div>
            <p className="text-xs text-muted-foreground">
              {estimates.length > 0 ? Math.round((acceptedEstimates / estimates.length) * 100) : 0}% conversion rate
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-brand-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalEstimateValue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Estimated revenue</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search estimates by customer or project name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Loading and Error States */}
      {loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-4">Loading estimates...</div>
          </CardContent>
        </Card>
      )}
      
      {error && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-4 text-red-600">
              Error loading estimates: {error.detail || error.message || 'Unknown error'}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estimates Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Estimates ({filteredEstimates.length})</CardTitle>
          <CardDescription>
            Track the status of your project estimates and proposals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer & Project</TableHead>
                <TableHead>Total Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created Date</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEstimates.map((estimate) => (
                <TableRow key={estimate.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{estimate.client_name || 'No Client Name'}</div>
                      <div className="text-sm text-gray-600">{estimate.project_name || 'No Project Name'}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center font-medium text-green-600">
                      <DollarSign className="mr-1 h-4 w-4" />
                      {estimate.total.toLocaleString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${getStatusColor(estimate.status)} text-white flex items-center`}>
                      {getStatusIcon(estimate.status)}
                      <span className="ml-2 capitalize">{estimate.status}</span>
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center text-sm">
                      <Calendar className="mr-1 h-3 w-3" />
                      {estimate.created_at ? new Date(estimate.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {estimate.valid_until ? new Date(estimate.valid_until).toLocaleDateString() : 'No Expiry'}
                      {estimate.valid_until && new Date(estimate.valid_until) < new Date() && (
                        <div className="text-red-600 text-xs">Expired</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedEstimate(estimate)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl">
                          <DialogHeader>
                            <DialogTitle>Estimate Details</DialogTitle>
                            <DialogDescription>
                              {selectedEstimate?.project_name} for {selectedEstimate?.client_name}
                            </DialogDescription>
                          </DialogHeader>
                          {selectedEstimate && (
                            <div className="space-y-6">
                              {/* Estimate Header */}
                              <div className="grid grid-cols-2 gap-4">
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="text-base">Estimate Information</CardTitle>
                                  </CardHeader>
                                  <CardContent className="space-y-2">
                                    <div><strong>Customer:</strong> {selectedEstimate.client_name}</div>
                                    <div><strong>Project:</strong> {selectedEstimate.project_name}</div>
                                    <div><strong>Created:</strong> {selectedEstimate.created_at ? new Date(selectedEstimate.created_at).toLocaleDateString() : 'N/A'}</div>
                                    <div><strong>Valid Until:</strong> {selectedEstimate.valid_until ? new Date(selectedEstimate.valid_until).toLocaleDateString() : 'No Expiry'}</div>
                                    <div><strong>Status:</strong> 
                                      <Badge className={`ml-2 ${getStatusColor(selectedEstimate.status)} text-white`}>
                                        {selectedEstimate.status.charAt(0).toUpperCase() + selectedEstimate.status.slice(1)}
                                      </Badge>
                                    </div>
                                  </CardContent>
                                </Card>
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="text-base">Total Summary</CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    <div className="text-right">
                                      <div className="text-3xl font-bold text-green-600">
                                        ${selectedEstimate.total.toLocaleString()}
                                      </div>
                                      <div className="text-sm text-gray-600">Total Estimate</div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>

                              {/* Line Items */}
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base">Estimate Breakdown</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Description</TableHead>
                                        <TableHead className="text-right">Quantity</TableHead>
                                        <TableHead className="text-right">Rate</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {(selectedEstimate.work_items || selectedEstimate.items || []).map((item, index) => (
                                        <TableRow key={index}>
                                          <TableCell>{item.description || item.name}</TableCell>
                                          <TableCell className="text-right">{item.quantity}</TableCell>
                                          <TableCell className="text-right">${(item.rate || item.price || 0).toLocaleString()}</TableCell>
                                          <TableCell className="text-right font-medium">${(item.amount || item.line_total || 0).toLocaleString()}</TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow className="border-t-2">
                                        <TableCell colSpan={3} className="font-medium text-right">Total:</TableCell>
                                        <TableCell className="text-right font-bold text-lg text-green-600">
                                          ${selectedEstimate.total.toLocaleString()}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </CardContent>
                              </Card>

                              {/* Actions */}
                              <div className="flex space-x-2">
                                <Button className="bg-brand-600 hover:bg-brand-700">
                                  <Send className="mr-2 h-4 w-4" />
                                  Send to Customer
                                </Button>
                                <Button 
                                  variant="outline"
                                  onClick={() => handleDownloadPDF(selectedEstimate.id, selectedEstimate.project_name)}
                                >
                                  <Download className="mr-2 h-4 w-4" />
                                  Download PDF
                                </Button>
                                <Link to={`/estimates/${selectedEstimate.id}/edit`}>
                                  <Button variant="outline">
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit Estimate
                                  </Button>
                                </Link>
                                <Button 
                                  variant="outline" 
                                  onClick={() => handleDeleteEstimate(selectedEstimate.id, selectedEstimate.project_name || 'Untitled')}
                                  disabled={deleteEstimateMutation.isPending}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Estimate
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      <Link to={`/estimates/${estimate.id}/edit`}>
                        <Button size="sm" variant="outline">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button size="sm" variant="outline">
                        <Send className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDeleteEstimate(estimate.id, estimate.project_name || 'Untitled')}
                        disabled={deleteEstimateMutation.isPending}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Estimate</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the estimate "{estimateToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={cancelDelete}
              disabled={deleteEstimateMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteEstimate}
              disabled={deleteEstimateMutation.isPending}
            >
              {deleteEstimateMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}